import React, { useEffect, useState } from 'react';
import { Plus, RotateCcw } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import {
  newGroupTimelineColorEntry,
  type GroupTimelineColorEntry,
  type GroupTimelineColorEntryKind,
} from '../../utils/groupTimelineCanonicalColors';
import {
  resetGroupTimelineColorsToSeed,
  saveGroupTimelineColors,
  subscribeGroupTimelineColors,
} from '../../utils/groupTimelineColorsSettings';

const GroupTimelineColorsSettingsCard: React.FC = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<GroupTimelineColorEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => subscribeGroupTimelineColors(doc => setEntries(doc.entries)), []);

  const actor = () => ({
    uid: user?.id || '',
    name: user?.name || user?.email || 'User',
  });

  const save = async (next: GroupTimelineColorEntry[]) => {
    setSaving(true);
    setStatus('idle');
    try {
      await saveGroupTimelineColors(next, actor());
      setEntries(next);
      setStatus('saved');
      window.setTimeout(() => setStatus('idle'), 2000);
    } catch (err) {
      console.error('[GroupTimelineColorsSettingsCard] save', err);
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (id: string, patch: Partial<GroupTimelineColorEntry>) => {
    setEntries(prev => prev.map(e => (e.id === id ? { ...e, ...patch } : e)));
  };

  return (
    <section className="settings-ops-section" aria-label="All-projects timeline colors">
      <h4 className="settings-ops-section-title">All-projects timeline — group colors</h4>
      <p className="setting-description">
        When the All Projects Gantt uses <strong>Color: Group</strong>, bars and group dots use these colors by
        group or milestone task name — the same on every project. Per-project group colors in Firestore apply only
        when no row matches here.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="settings-table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th>Label</th>
              <th>Kind</th>
              <th>Match key</th>
              <th>Color</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.map(row => (
              <tr key={row.id}>
                <td>
                  <input
                    value={row.label}
                    onChange={e => updateRow(row.id, { label: e.target.value })}
                    style={{ width: '100%', minWidth: 120 }}
                  />
                </td>
                <td>
                  <select
                    value={row.kind}
                    onChange={e =>
                      updateRow(row.id, { kind: e.target.value as GroupTimelineColorEntryKind })
                    }
                  >
                    <option value="group">Group</option>
                    <option value="task">Task</option>
                  </select>
                </td>
                <td>
                  <input
                    value={row.matchKey}
                    onChange={e => updateRow(row.id, { matchKey: e.target.value.toLowerCase() })}
                    style={{ width: '100%', minWidth: 140 }}
                  />
                </td>
                <td>
                  <input
                    type="color"
                    value={row.color}
                    onChange={e => updateRow(row.id, { color: e.target.value })}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setEntries(prev => prev.filter(e => e.id !== row.id))}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="um-btn-secondary"
          onClick={() =>
            setEntries(prev => [...prev, newGroupTimelineColorEntry('group', 'New group', '#6B7280')])
          }
        >
          <Plus size={14} aria-hidden /> Add row
        </button>
        <button
          type="button"
          className="um-btn-secondary"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await resetGroupTimelineColorsToSeed(actor());
            } finally {
              setSaving(false);
            }
          }}
        >
          <RotateCcw size={14} aria-hidden /> Reset to defaults
        </button>
        <button type="button" className="save-button" disabled={saving} onClick={() => void save(entries)}>
          {saving ? 'Saving…' : status === 'saved' ? '✓ Saved' : status === 'error' ? 'Retry save' : 'Save colors'}
        </button>
      </div>
    </section>
  );
};

export default GroupTimelineColorsSettingsCard;
